import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { Field, ErrorMessage, FieldArray } from 'formik';
import { Locale, ProportionType } from '../types';
import { ReactComponent as RecIcon } from 'assets/icons/recommendation.svg';
import { ReactComponent as DeleteIcon } from 'assets/icons/trash.svg';
import Prompt from 'components/Modal/Prompt';
import Select from './Select';
import LogicCreator, { CreatorType } from '../LogicCreator/LogicCreator';
import ProportionCreator from '../ProportionCreator/ProportionCreator';
import Checkbox from '../Common/Checkbox';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #e3e5e8;
  background-color: white;
  border-radius: 4px;
  padding: 2rem;
  margin: 1rem 0;
  position: relative;
`;

const Button = styled.button`
  height: 2rem;
  outline: none;
  border: none;
  background-color: rgb(31, 93, 234) !important;
  max-width: 10rem;
  margin: 1rem 0 0;
  color: white;
  border-radius: 5px;
  font-weight: 500;
  cursor: pointer;
`;

const Delete = styled.span`
  position: absolute;
  cursor: pointer;
  top: 1rem;
  right: 1rem;
`;

const Box = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
`;

const Title = styled.input`
  font-size: 1.125rem;
  border: none;
  border-bottom: 1px solid black;
  outline: none;
  width: 80%;
`;

const RecommnedationTitle = styled.p`
  color: #253858;
  font-size: 1rem;
  fon-weight: 500;
  margin: 0 0.5rem 0 0.5rem;
`;

const LogicBox = styled.div`
  display: flex;
  width: 100;
  flex-direction: column;
  margin-bottom: 1rem;
`;

const HeaderBox = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const LongDescription = styled.textarea`
  border: none;
  border-bottom: 1px solid black;
  outline: none;
  width: 80%;
  height: 5rem;
  margin-top: 1rem;
  min-height: unset !important;
  background-color: white;
`;

const Error = styled.span`
  color: red;
`;

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 1.8rem;
`;

const BoxWrapper = styled.div`
  display: flex;
  margin-bottom: 2rem;
`;

interface Props {
  name: string;
  currentLang: Locale;
  remove: () => void;
  isRecommendation: boolean;
  visibleIfType: CreatorType;
  questionsList: string[];
  proportions: null | ProportionType[];
  isActive: boolean;
}

const Interpretation = ({
  name,
  currentLang,
  remove,
  isRecommendation,
  visibleIfType,
  questionsList,
  proportions,
  isActive,
}: Props) => {
  const [isDeletePrompt, setIsDeletePrompt] = useState(false);
  const [isVisibleIf, setVisibleIf] = useState(false);
  const [isProportionsOpen, setProportionOpen] = useState(false);

  const questionAndProportions = proportions
    ? [...questionsList, ...proportions.map(({ name }) => name)]
    : questionsList;

  return (
    <Content>
      <Prompt
        isOpen={isDeletePrompt}
        title="Remove interpretation?"
        onCancel={() => setIsDeletePrompt(false)}
        onConfirm={() => {
          remove();
          setIsDeletePrompt(false);
        }}
      />
      <Delete onClick={() => setIsDeletePrompt(true)}>
        <DeleteIcon />
      </Delete>
      <BoxWrapper>
        <Box>
          <HeaderBox>
            <Field
              name={`${name}.icon`}
              component={Select}
              currentValue={visibleIfType}
            />

            {currentLang === 'en' && (
              <FieldWrapper>
                <Field
                  name={`${name}.title.en`}
                  as={Title}
                  placeholder="Title"
                />
                <ErrorMessage component={Error} name={`${name}.title.en`} />
              </FieldWrapper>
            )}
            {currentLang === 'de' && (
              <FieldWrapper>
                <Field
                  name={`${name}.title.de`}
                  as={Title}
                  placeholder="Title"
                />
                <ErrorMessage component={Error} name={`${name}.title.de`} />
              </FieldWrapper>
            )}
            {currentLang === 'fr' && (
              <FieldWrapper>
                <Field
                  name={`${name}.title.fr`}
                  as={Title}
                  placeholder="Title"
                />
                <ErrorMessage component={Error} name={`${name}.title.fr`} />
              </FieldWrapper>
            )}
            {currentLang === 'it' && (
              <FieldWrapper>
                <Field
                  name={`${name}.title.it`}
                  as={Title}
                  placeholder="Title"
                />
                <ErrorMessage component={Error} name={`${name}.title.it`} />
              </FieldWrapper>
            )}
          </HeaderBox>
          {currentLang === 'en' && (
            <FieldWrapper>
              <Field
                name={`${name}.interpretationText.en`}
                as={LongDescription}
                placeholder="Interpretation"
              />
              <ErrorMessage
                component={Error}
                name={`${name}.interpretationText.en`}
              />
            </FieldWrapper>
          )}
          {currentLang === 'de' && (
            <FieldWrapper>
              <Field
                name={`${name}.interpretationText.de`}
                as={LongDescription}
                placeholder="Interpretation"
              />
              <ErrorMessage
                component={Error}
                name={`${name}.interpretationText.de`}
              />
            </FieldWrapper>
          )}
          {currentLang === 'fr' && (
            <FieldWrapper>
              <Field
                name={`${name}.interpretationText.fr`}
                as={LongDescription}
                placeholder="Interpretation"
              />
              <ErrorMessage
                component={Error}
                name={`${name}.interpretationText.fr`}
              />
            </FieldWrapper>
          )}
          {currentLang === 'it' && (
            <FieldWrapper>
              <Field
                name={`${name}.interpretationText.it`}
                as={LongDescription}
                placeholder="Interpretation"
              />
              <ErrorMessage
                component={Error}
                name={`${name}.interpretationText.it`}
              />
            </FieldWrapper>
          )}
        </Box>
        <Box>
          <HeaderBox>
            <RecIcon />
            <RecommnedationTitle>Recommendation</RecommnedationTitle>
            <Checkbox
              fieldName={`${name}.isRecommendation`}
              isChecked={isRecommendation}
              label=""
            />
          </HeaderBox>
          {isRecommendation && (
            <>
              {currentLang === 'en' && (
                <FieldWrapper>
                  <Field
                    name={`${name}.recommendationText.en`}
                    as={LongDescription}
                    placeholder="Recommendation"
                  />
                  <ErrorMessage
                    component={Error}
                    name={`${name}.recommendationText.en`}
                  />
                </FieldWrapper>
              )}
              {currentLang === 'de' && (
                <FieldWrapper>
                  <Field
                    name={`${name}.recommendationText.de`}
                    as={LongDescription}
                    placeholder="Recommendation"
                  />
                  <ErrorMessage
                    component={Error}
                    name={`${name}.recommendationText.de`}
                  />
                </FieldWrapper>
              )}
              {currentLang === 'fr' && (
                <FieldWrapper>
                  <Field
                    name={`${name}.recommendationText.fr`}
                    as={LongDescription}
                    placeholder="Recommendation"
                  />
                  <ErrorMessage
                    component={Error}
                    name={`${name}.recommendationText.fr`}
                  />
                </FieldWrapper>
              )}
              {currentLang === 'it' && (
                <FieldWrapper>
                  <Field
                    name={`${name}.recommendationText.it`}
                    as={LongDescription}
                    placeholder="Recommendation"
                  />
                  <ErrorMessage
                    component={Error}
                    name={`${name}.recommendationText.it`}
                  />
                </FieldWrapper>
              )}
            </>
          )}
        </Box>
      </BoxWrapper>
      <LogicBox>
        <Button type="button" onClick={() => setVisibleIf(!isVisibleIf)}>
          Visible if
        </Button>

        {isVisibleIf && (
          <LogicCreator
            fieldName={`${name}`}
            visibleIfType={visibleIfType}
            questionsList={questionAndProportions}
          />
        )}
        <Button
          type="button"
          onClick={() => setProportionOpen(!isProportionsOpen)}
        >
          Proportions
        </Button>
        {isProportionsOpen && (
          <FieldArray name={`${name}.proportions`}>
            {({ remove, push, form: { setFieldValue } }) => {
              return (
                <ProportionCreator
                  push={push}
                  remove={remove}
                  questionsList={questionsList}
                  proportions={proportions || []}
                  name={`${name}.proportions`}
                  setFieldValue={setFieldValue}
                />
              );
            }}
          </FieldArray>
        )}
      </LogicBox>

      <Checkbox
        fieldName={`${name}.isActive`}
        label="is Active"
        isChecked={isActive}
      />
    </Content>
  );
};

export default Interpretation;
